import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import { EntityType } from './types';
import { getEntityType } from './utils';

import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import ChevronRightIcon from 'modules/common/components/Icons/ChevronRightIcon';
import ChevronUpIcon from 'modules/common/components/Icons/ChevronUpIcon';
import CloseXIcon from 'modules/common/components/Icons/CloseXIcon';
import InvoiceIcon from 'modules/common/components/Icons/InvoiceIcon';
import NavigationPayrollIcon from 'modules/common/components/Icons/NavigationPayrollIcon';
import NavigationReimbursementsIcon from 'modules/common/components/Icons/NavigationReimbursementsIcon';
import ReceiptIcon from 'modules/common/components/Icons/ReceiptIcon';
import ReceiptTaxIcon from 'modules/common/components/Icons/ReceiptTaxIcon';
import { useMember } from 'modules/common/queries/member';
import { useIMCContext } from 'modules/IMC/common/context';
import usePayrollHomePath from 'modules/Payroll/hooks/usePayrollHomePath';

/**
 * Floating "Try it for yourself" checklist (Mercury-style). Stays mounted globally inside
 * DemoApp so it survives SPA navigation. The X button collapses it to a small floating
 * launcher icon (it never fully disappears so the user can always bring it back).
 * Open/minimized state lives in sessionStorage so it resets on hard reload.
 */

const STORAGE_KEY = 'demo.tryItChecklist';

type Task = {
    id: string;
    label: string;
    path: string;
    Icon: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large'; sx?: object }>;
};

// "Run payroll" only applies to the S-Corp persona — LLC members don't have payroll in
// the real product, so the sidebar omits it and the checklist should too.
const TASKS: Task[] = [
    {
        id: 'categorize',
        label: 'Categorize a transaction',
        path: '/bookkeeping/transactions',
        Icon: ReceiptIcon,
    },
    {
        id: 'reimbursement',
        label: 'Submit a monthly reimbursement',
        // No params → defaults to the current month (seeded non-zero by remapReimbursementsToNow).
        path: '/bookkeeping/reimbursement-form',
        Icon: NavigationReimbursementsIcon,
    },
    {
        id: 'payroll',
        label: 'Run payroll',
        path: '/payroll',
        Icon: NavigationPayrollIcon,
    },
    {
        id: 'tax',
        label: 'View your tax return',
        path: '/tax/business/2025',
        Icon: ReceiptTaxIcon,
    },
    {
        id: 'invoice',
        label: 'Send an invoice',
        path: '/bookkeeping/invoicing/new',
        Icon: InvoiceIcon,
    },
];

function getTasksFor(entityType: EntityType): Task[] {
    if (entityType === 'scorp') {
        return TASKS;
    }
    return TASKS.filter((task) => task.id !== 'payroll');
}

type DisplayMode = 'open' | 'collapsed' | 'minimized';

function readMode(): DisplayMode {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw === 'collapsed' || raw === 'minimized') {
            return raw;
        }
    } catch {
        // ignore corrupt state
    }
    return 'open';
}

function writeMode(mode: DisplayMode) {
    try {
        sessionStorage.setItem(STORAGE_KEY, mode);
    } catch {
        // ignore quota / private-mode errors
    }
}

export default function DemoTryItChecklist() {
    const history = useHistory();
    const [mode, setMode] = useState<DisplayMode>(readMode);
    const { data: member } = useMember();
    // Whichever payroll dashboard the demo is showing, not always the old one at `/payroll`.
    const payrollHome = usePayrollHomePath();
    const tasks = getTasksFor(getEntityType(member)).map((task) =>
        task.id === 'payroll' ? { ...task, path: payrollHome } : task
    );

    // A surface with a sticky footer hides the help chat button so it can't cover the footer's
    // action; this checklist sits in the same corner, so it steps aside for the same reason.
    const { isHelpAgentFabHidden } = useIMCContext();

    const update = (next: DisplayMode) => {
        setMode(next);
        writeMode(next);
    };

    const onTaskClick = (path: string) => {
        // Split into pathname + search so React Router parses query strings correctly.
        const [pathname, search] = path.split('?');
        history.push({ pathname, search: search ? `?${search}` : '' });
    };

    if (isHelpAgentFabHidden) {
        return null;
    }

    if (mode === 'minimized') {
        return (
            <IconButton
                onClick={() => update('open')}
                aria-label="Open Try Collective checklist"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: (theme) => theme.zIndex.modal,
                    width: 56,
                    height: 56,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    boxShadow: 6,
                    '&:hover': { bgcolor: 'primary.dark' },
                }}>
                <ReceiptIcon />
            </IconButton>
        );
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                width: 440,
                maxWidth: 'calc(100vw - 48px)',
                zIndex: (theme) => theme.zIndex.modal,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 3,
                boxShadow: 6,
                overflow: 'hidden',
            }}>
            <Stack direction="row" alignItems="center" sx={{ px: 3, py: 2 }}>
                <Box
                    role="button"
                    tabIndex={0}
                    aria-expanded={mode === 'open'}
                    onClick={() => update(mode === 'open' ? 'collapsed' : 'open')}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            update(mode === 'open' ? 'collapsed' : 'open');
                        }
                    }}
                    sx={{ flex: 1, cursor: 'pointer', userSelect: 'none' }}>
                    <Typography variant="h6">Take Collective for a spin</Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={() => update(mode === 'open' ? 'collapsed' : 'open')}
                    aria-label={mode === 'open' ? 'Collapse checklist' : 'Expand checklist'}>
                    {mode === 'open' ? <ChevronDownIcon fontSize="small" /> : <ChevronUpIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={() => update('minimized')} aria-label="Minimize checklist">
                    <CloseXIcon fontSize="small" />
                </IconButton>
            </Stack>
            {mode === 'open' && (
                <Box>
                    {tasks.map((task) => {
                        const { Icon } = task;
                        return (
                            <Stack
                                key={task.id}
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                role="button"
                                tabIndex={0}
                                onClick={() => onTaskClick(task.path)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onTaskClick(task.path);
                                    }
                                }}
                                sx={{
                                    px: 3,
                                    py: 2.25,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.15s ease',
                                    '&:hover': { bgcolor: 'cement.50' },
                                }}>
                                <Icon sx={{ color: 'text.secondary' }} />
                                <Typography variant="body1" sx={{ flex: 1 }}>
                                    {task.label}
                                </Typography>
                                <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                            </Stack>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
